import { Trash2, Undo2 } from "lucide-react";
import { TRY, NUM, fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { occKey, r2 } from "@/components/returns/util";
import { LatestBadge, PriceHistory } from "@/components/returns/PriceHistory";

const occLabel = (o, unit, latest) => `${latest ? "EN SON · " : ""}${fmtDate(o.date)} · ${o.belge_no} · ${NUM(o.quantity)} ${unit} · ${TRY(o.net_unit)}`;

function LineRow({ l, i, onUpdate, onRemove, onSwitch }) {
  const o = l.occ;
  const qty = Number(l.qty);
  const bad = !(qty > 0);
  const over = qty > o.remaining + 1e-6;
  const isLatest = occKey(l.occs[0]) === occKey(o);
  const net = r2((qty || 0) * o.net_unit);
  return (
    <tr className="border-b last:border-0 align-top" data-testid={`ret-line-${i}`}>
      <td className="px-3 py-2 text-xs text-muted-foreground tabular-nums">{i + 1}</td>
      <td className="px-3 py-2 min-w-[200px]">
        <div className="font-semibold text-[13px] leading-snug">{l.name}</div>
        <div className="text-[11px] text-muted-foreground">{l.code && <span className="font-mono">{l.code}</span>}{o.discount_rate > 0 && <Badge variant="outline" className="ml-1.5 text-[10px] py-0 border-primary/40 text-primary">isk. %{NUM(o.discount_rate)}</Badge>}</div>
      </td>
      <td className="px-3 py-2 min-w-[280px]">
        <Select value={occKey(o)} onValueChange={(k) => onSwitch(l, l.occs.find((x) => occKey(x) === k))}>
          <SelectTrigger className="h-8 text-xs" data-testid={`ret-line-invoice-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {l.occs.map((x, k) => <SelectItem key={occKey(x)} value={occKey(x)} className="text-xs">{occLabel(x, l.unit, k === 0)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-[11px] mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`ret-line-avail-${i}`}>
          {isLatest ? <LatestBadge /> : <span className="text-amber-700 font-semibold">eski fatura seçili</span>}
          <span>Faturada <b className="text-foreground" data-testid={`ret-line-invqty-${i}`}>{NUM(o.quantity)} {l.unit}</b> alındı</span>
          {o.returned > 0 && <span className="text-orange-700">kesilmiş iade {NUM(o.returned)}</span>}
          {o.in_draft > 0 && <span className="text-orange-700">taslakta {NUM(o.in_draft)}</span>}
        </div>
        <PriceHistory occs={l.occs} unit={l.unit} testid={`ret-line-history-${i}`} />
      </td>
      <td className="px-3 py-2 w-32">
        <Input type="number" min="0" step="any" inputMode="decimal" value={l.qty} onChange={(e) => onUpdate(l.uid, { qty: e.target.value })}
          className={cn("h-8 text-right tabular-nums", bad && "border-red-400", !bad && over && "border-amber-400")} aria-label="İade miktarı" data-testid={`ret-line-qty-${i}`} />
        {!bad && over && <div className="text-[10px] text-amber-700 mt-0.5 leading-tight" data-testid={`ret-line-over-${i}`}>Faturadaki kalan miktardan ({NUM(o.remaining)}) fazla — yine de iade edilebilir</div>}
      </td>
      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap"><span className={cn("font-semibold", isLatest && "text-emerald-700")}>{TRY(o.net_unit)}</span><div className="text-[10px] text-muted-foreground">+KDV %{NUM(o.tax_rate)}</div></td>
      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap font-bold" data-testid={`ret-line-total-${i}`}>{TRY(r2(net * (1 + o.tax_rate / 100)))}</td>
      <td className="px-2 py-2 text-right"><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onRemove(l.uid)} title="Kalemi çıkar" data-testid={`ret-line-remove-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></td>
    </tr>
  );
}

export function ReturnLinesTable({ lines, onUpdate, onRemove, onSwitch }) {
  if (!lines.length) {
    return (
      <div className="rounded-xl border border-dashed py-10 text-center text-sm text-muted-foreground" data-testid="ret-lines-empty">
        <Undo2 className="h-6 w-6 mx-auto mb-2 text-orange-400" />
        İade edilecek ürünün kodunu yazıp Enter'a basın — en son alındığı fatura, fiyatı ve fatura numarası otomatik gelir.
      </div>
    );
  }
  return (
    <div className="rounded-xl border overflow-x-auto" data-testid="ret-lines">
      <table className="w-full text-sm min-w-[900px]">
        <thead className="bg-slate-800 text-white text-[10px] uppercase tracking-wider">
          <tr>
            <th className="px-3 py-2 w-8 text-left">#</th>
            <th className="px-3 py-2 text-left">Ürün</th>
            <th className="px-3 py-2 text-left">Alış Faturası · Fiyat Geçmişi</th>
            <th className="px-3 py-2 text-right">İade Miktarı</th>
            <th className="px-3 py-2 text-right">Net Birim</th>
            <th className="px-3 py-2 text-right">Tutar (KDV'li)</th>
            <th className="px-2 py-2 w-10" />
          </tr>
        </thead>
        <tbody>{lines.map((l, i) => <LineRow key={l.uid} l={l} i={i} onUpdate={onUpdate} onRemove={onRemove} onSwitch={onSwitch} />)}</tbody>
      </table>
    </div>
  );
}
